import React from 'react';
import { Router, Route, IndexRoute, browserHistory } from 'react-router';
import Analytics from './services/analytics';
import { Helmet } from 'react-helmet';

import LoginStore from './stores/login-store';
import PostHogService from './services/posthog';
import PostHogEvents from './constants/posthog-events';
import { isFreeTrialExpired } from './middleware/middleware';

import LoggedOutTemplate from './components/loggedout-template';
import LoggedInTemplate from './components/loggedin-template';
import GlobalPopup from './components/popups/global-popup';

import HomePage from './pages/homepage/homepage';
import Pricing from './pages/pricing/marketing-pricing';
import ContactUs from './pages/contact-us/contact';
import Faqs from './pages/contact-us/faqs';
import About from './pages/about/marketing-about';
import IbrahimImam from './pages/about/ibrahim-imam';
import { isFeatureEnabled } from './constants/features';
import Axon from './pages/axon/marketing-axon';
import Credits from './pages/about/credits';
import Terms from './pages/legals/tcs';
import Privacy from './pages/legals/privacy';
import Data from './pages/legals/data';
import SurfingTheBrain from './pages/surfing-the-brain/marketing-surfing-the-brain';

import PageNotFound from './pages/404/404';
import IndexPage from './components/pages/marketing-explore-page';
import Checklist from './components/pages/marketing-checklist-page';
import SearchMarketing from './pages/searches/search-marketing';
import BookmarksMarketing from './pages/bookmarks/bookmarks-marketing';
import NotesMarketing from './pages/notes/notes-marketing';
import UserDetails from './pages/settings/user-details';
import UserGuide from './pages/settings/user-guide-marketing';
import UserChangePassword from './pages/settings/user-change-password';
import UserDelete from './pages/settings/user-delete';
import IndexRedirector from './components/index-redirector';
import ChatPage from './pages/chat/chat-page';
import sitemapRedirector from './components/sitemap-redirector';

// the API decides when a sign-in ends (30 days unused); LoginStore signs out once it refuses the token
const checkSession = (nextState, replace, next) => {
    if (!LoginStore.isLoggedIn()) replace(`/home?modal=login`);
    next();
};

// Axon is a paid feature, so expired-trial users get the upgrade modal instead
const checkAxonAccess = (nextState, replace, next) => {
    checkSession(nextState, replace, () => {
        if (LoginStore.isLoggedIn() && isFreeTrialExpired(LoginStore.getSessionUserData())) {
            replace('/?modal=upgrade');
        }
        next();
    });
};

function logPageView() {
    Analytics.pageView();
    PostHogService.capture(PostHogEvents.PAGE_VIEW);
    if (typeof window !== 'undefined' && !window.location.hash) {
        window.scrollTo(0, 0);
    }
}

class App extends React.Component {
    render() {
        return (
            <div className="app">
                <Helmet defaultTitle="Neurochecklists | Evidence-Based Neurology Checklists" titleTemplate="%s">
                    <html lang="en" />
                    {/* No default description: the server renders one for every page, and a second,
                        different one here showed up on any page that doesn't set its own */}
                    <meta property="og:site_name" content="Neurochecklists" />
                    <meta property="og:type" content="website" />
                    <meta property="og:image" content="https://neurochecklists.com/images/og-default.png" />
                    <meta property="og:image:width" content="1200" />
                    <meta property="og:image:height" content="630" />
                    <meta property="og:locale" content="en_GB" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:site" content="@neurochecklists" />
                    <link rel="canonical" href={'https://neurochecklists.com' + window.location.pathname} />
                </Helmet>
                <Router history={browserHistory} onUpdate={logPageView}>
                    <Route name="sitemap.xml" path="/sitemap.xml" component={sitemapRedirector} />
                    <Route name="dashboard" component={LoginStore.isLoggedIn() ? LoggedInTemplate : LoggedOutTemplate}>
                        <Route name="bookmarks" path="bookmarks" component={BookmarksMarketing} onEnter={checkSession} />
                        <Route name="notes" path="notes" component={NotesMarketing} onEnter={checkSession} />
                        <Route path="search" component={SearchMarketing} />
                        <Route name="legacy-index" path="index/*" component={IndexRedirector}></Route>
                        <Route name="legacy-index-root" path="index" component={IndexRedirector}></Route>
                        <Route name="explore" path="explore">
                            <IndexRoute component={IndexPage} />
                            <Route name="chapters" path="chapters/:id" component={IndexPage} />
                            <Route name="checklists" path="checklists/:id" component={Checklist} />
                        </Route>
                        <Route name="surfing-the-brain" path="surfing-the-brain" component={SurfingTheBrain} />
                        <Route name="chat" path="chat" component={ChatPage} onEnter={checkAxonAccess} />
                        <Route name="chat-conversation" path="chat/:conversationId" component={ChatPage} onEnter={checkAxonAccess} />
                        <Route name="account">
                            <Route path="account/update-details" component={UserDetails} onEnter={checkSession} />
                            <Route path="account/guide" component={UserGuide} onEnter={checkSession} />
                            <Route
                                path="account/change-password"
                                component={UserChangePassword}
                                onEnter={checkSession}
                            />
                            <Route path="account/delete-account" component={UserDelete} onEnter={checkSession} />
                        </Route>
                    </Route>

                    <Route
                        name="Neurochecklists"
                        component={LoginStore.isLoggedIn() ? LoggedInTemplate : LoggedOutTemplate}
                    >
                        <Route name="home" path="/" component={HomePage} />
                        <Route name="home" path="/home" component={HomePage} />
                        <Route name="contact" path="contact" component={ContactUs} />
                        <Route name="faqs" path="faqs" component={Faqs} />
                        <Route name="pricing" path="pricing" component={Pricing} />
                        <Route name="about" path="about" component={About} />
                        {isFeatureEnabled('authorPage') && (
                            <Route name="ibrahim-imam" path="about/ibrahim-imam" component={IbrahimImam} />
                        )}
                        <Route name="axon" path="axon" component={Axon} />
                        <Route name="tcs" path="terms-and-conditions" component={Terms} />
                        <Route name="privacy" path="privacy-policy" component={Privacy} />
                        <Route name="data" path="data-policy" component={Data} />
                        <Route name="credits" path="site-credits" component={Credits} />
                        <Route path="*" component={PageNotFound} />
                    </Route>
                </Router>
                <GlobalPopup location={window.location}></GlobalPopup>
            </div>
        );
    }
}

export default App;
